import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import styles from './embed.module.scss';
import ModalContent from '../../../Modal/ModalContent';
import ModalFooter from '../../../Modal/ModalFooter';
import VideoService from '../../../../service/VideoService';
import Checkbox from '../Checkbox/RadioButton';
import SharingModalButton from '../SharingModalButton/SharingModalButton';
import ResponsiveSettings from './ResponsiveSettings/ResponsiveSettings';
import copy from '../../../../utils/copy-html-to-clipboard';
import configs from '../../../../../../conf';
import SharingFooter from '../SharingFooter';
import { useTranslation } from 'react-i18next';
import ResourceHelper from '../../../../utils/ResourceHelper';
import Spinner from '../../../../../common/components/Spinner';
import useFetchFoldersList from '../../../../hooks/useFetchFoldersList';
import { useUserContext } from '../../../../context/UserContext';
import { FOLDER_USER_ROLE } from '../../../../constants/UserRoles';
import { approvalStatuses } from '../../../../features/Dashboard/components/VideosFilter/constants';

const config = configs[process.env.APP_CONF];
const lastRenderEngineDeploy = new Date(config.renderEngineDeployTimestamp);

const initialChecked = {
  fixed: { checked: false },
  responsive: { checked: true },
};

const responsiveGifSize = {
  width: '100%',
  height: 'auto',
};

const fixedGifSize = {
  width: 640,
  height: 400,
};

let tries = 0;

function generateVideoHTMLCopyGif({ url, thumbnail, height, width, maxWidth, maxHeight }) {
  return (
    `<p style="background-color: transparent; font-size: inherit; margin: 0; text-align: left;">` +
    `<a href='${url}'>` +
    `<img src='${thumbnail}' style="height: ${height};width: ${width};max-width: ${maxWidth};max-height: ${maxHeight};margin: auto; padding-top: 5px;" alt="Loading video..."/></a>` +
    `<br /><a href='${url}' style="font-size: inherit;" target="_blank" rel="noreferrer noopener">Watch video</a><br /></p>`
  );
}

function generateVideoHTMLEmbedded({ url, thumbnail, height, width, maxWidth, maxHeight }) {
  if (height === 'auto') height = '100%';
  return `<iframe style="position: absolute; top: 0; left: 0; width: ${width}; height: ${height}; " src="${url}?embedded" frameborder="0" allowfullscreen="allowfullscreen"></iframe>`;
}

const Embed = ({ videoId, videoPassword = '', isSkipAuthToken }) => {
  const [video, setVideo] = useState({});
  const [checked, setChecked] = useState(initialChecked);
  const [clickedCopyEmbed, setClickedCopyEmbed] = useState(false);
  const [clickedCopyGif, setClickedCopyGif] = useState(false);
  const [gifSize, setGifSize] = useState(responsiveGifSize);
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView, setIsMobileView] = useState(mediaMatch.matches);
  const [thumbnailErrors, setThumbnailErrors] = useState(0);
  const { t } = useTranslation();
  const [isCanChangeVideoPassword, setIsCanChangeVideoPassword] = useState(false);

  const { allFolders } = useFetchFoldersList({});
  const { user, isApprovalFlowEnabled, isReviewerUser, logFirebaseEvent } = useUserContext();
  const isPreExistedStatus = useMemo(() => video?.approvalStatus === approvalStatuses.PRE_EXISTED.TYPE, [video]);

  useEffect(() => {
    const resetClickedCopyLink = mode => {
      setTimeout(() => {
        switch (mode) {
          case 'embed':
            setClickedCopyEmbed(false);
            break;
          case 'gif':
            setClickedCopyGif(false);
            break;
          default:
            break;
        }
      }, 3000);
    };
    clickedCopyEmbed && resetClickedCopyLink('embed');
    clickedCopyGif && resetClickedCopyLink('gif');
  }, [clickedCopyEmbed, clickedCopyGif]);

  async function getVideoDetails(videoPassword) {
    try {
      console.log('getVideoDetails', videoPassword);
      tries = tries + 1;
      console.log('tries', tries);

      let videoDetails = {};
      if (videoPassword?.length === 0) {
        videoDetails = await VideoService.getVideoDetails(videoId, isSkipAuthToken);
      } else {
        videoDetails = await VideoService.getProtectedVideo(videoId, videoPassword);
      }

      if (videoDetails === 404 && tries <= 100) {
        console.log('404, probing again in 3s');
        setTimeout(() => {
          getVideoDetails(videoPassword);
        }, 3000);
        return;
      }

      videoDetails.shareUrl = `${config.domain}/engage/${videoId}`;

      let disableGif =
        videoDetails.settings != null && 'disableGif' in videoDetails.settings ? videoDetails.settings.disableGif : false;

      // Grab thumbnails from the Story's resources
      const defaultThumbnail = ResourceHelper.getResourceByLabel(videoDetails, 'defaultAnimatedThumbnail');
      const customThumbnail = ResourceHelper.getResourceByLabel(videoDetails, 'customAnimatedThumbnail');

      if (!defaultThumbnail && !customThumbnail) {
        videoDetails.activeThumbnail = videoDetails.thumbnail;
      } else {
        let animatedThumbnail = !customThumbnail ? defaultThumbnail : customThumbnail;
        if (!disableGif && videoDetails.thumbnail) {
          videoDetails.activeThumbnail = animatedThumbnail?.url;
        } else {
          videoDetails.activeThumbnail = videoDetails?.thumbnail;
        }
      }

      setVideo(videoDetails);
      // If we received an incomplete story model, try to get new video details again in 3 seconds, max 4 times
      if (videoDetails.source == 'web' && videoDetails.thumbnail == null && tries <= 100) {
        console.log('No thumbnail, probing again in 3s');
        setTimeout(() => {
          getVideoDetails(videoPassword);
        }, 3000);
      }
    } catch (e) {
      console.log(e);
    }
  }

  useEffect(() => {
    if (!video.author || !user) {
      return;
    }

    if (isApprovalFlowEnabled && !isPreExistedStatus) {
      if (isReviewerUser) {
        setIsCanChangeVideoPassword(true);
      } else {
        setIsCanChangeVideoPassword(false);
      }

      return;
    }

    if (video.author.username === user.username) {
      setIsCanChangeVideoPassword(true);
    }

    if (video.author.username !== user.username && video.folder) {
      const currentFolder = allFolders.find(({ id }) => id === video.folder);

      const isFolderEditor = currentFolder?.users?.find(
        ({ username, folderRole }) => username === user.username && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE
      );
      const isFolderOwner = user.username === currentFolder?.owner?.username;

      setIsCanChangeVideoPassword(isFolderOwner || isFolderEditor);
    }
  }, [video, user, allFolders, isApprovalFlowEnabled, isReviewerUser, isPreExistedStatus]);

  useMemo(async () => {
    if (videoId) {
      getVideoDetails(videoPassword);
    }
  }, [videoId]);

  const handleChangeCheckbox = event => {
    const id = event.target.id;
    if (id === 'fixed' && !checked.fixed.checked) {
      setChecked({
        fixed: { checked: true },
        responsive: { checked: false },
      });

      setGifSize(fixedGifSize); // Set the gif size back to the original fixed dimensions
    }
    if (id === 'responsive' && !checked.responsive.checked) {
      setChecked({
        fixed: { checked: false },
        responsive: { checked: true },
      });

      setGifSize(responsiveGifSize); // Set the gif size back to the original responsive dimensions
    }
  };

  const handleCopyEmbedCode = async () => {
    setClickedCopyEmbed(true);
    setClickedCopyGif(false);

    const obj = {
      url: video.shareUrl,
      thumbnail: video.activeThumbnail,
      height: gifSize.height,
      width: gifSize.width,
      maxWidth: 'auto',
    };
    logFirebaseEvent('copy_embed_cl', { story_id: videoId });

    const html = generateVideoHTMLEmbedded(obj);
    copy(html);
  };

  const handleCopyGif = async () => {
    setClickedCopyGif(true);
    setClickedCopyEmbed(false);
    logFirebaseEvent('copy_gif_cl', { story_id: videoId });

    const obj = {
      url: video.shareUrl,
      thumbnail: video.activeThumbnail,
      height: gifSize.height,
      width: gifSize.width,
      maxWidth: '300px',
      maxHeight: '300px',
    };

    const html = generateVideoHTMLCopyGif(obj);
    copy(html, { asHtml: true });
  };

  const toggleConstInURL = url => {
    const RESIZED = '_resized';
    const thumbnailURL = new URL(url);
    let thumbnailSplitPath = thumbnailURL.pathname.split('.');
    if (thumbnailSplitPath[0].includes(RESIZED)) {
      thumbnailSplitPath[0] = thumbnailSplitPath[0].replace('_resized', '');
    } else {
      thumbnailSplitPath[0] += '_resized';
    }
    return thumbnailURL.origin + thumbnailSplitPath.join('.');
  };

  const handleMobileThumbnail = () => {
    if (video.source === 'mobile' && thumbnailErrors <= 1) {
      let activeThumbnail = video.activeThumbnail;
      if (thumbnailErrors === 0) {
        activeThumbnail = toggleConstInURL(video.activeThumbnail);
      } else {
        const toggledTumbnail = toggleConstInURL(video.activeThumbnail);
        activeThumbnail = toggledTumbnail.replace('.gif', '.jpg');
      }
      setVideo(prevState => ({ ...prevState, activeThumbnail }));
      setThumbnailErrors(prevState => prevState + 1);
    }
  };

  return (
    <>
      <ModalContent className={cx(styles.content)}>
        <h2 role='heading' tabIndex={0} aria-label={video?.name || ''} className={styles.title}>{video?.name || ' '}</h2>
        <div className={styles.wrapper}>
          <div className={styles.thumbnail}>
            {video.thumbnail ? <img src={video.activeThumbnail} alt="See my video here" onError={handleMobileThumbnail} /> : null}

            {!video.thumbnail && (!video.source || video.source == 'web') ? (
              <Spinner className={styles.spinner} width="24px" height="24px" size="2px" />
            ) : null}
          </div>
          <div className={styles.thumbnailSettings}>
            <div className={styles.copyGif}>
              <SharingModalButton
                onClick={handleCopyGif}
                clicked={clickedCopyGif}
                disabled={!video.thumbnail && (!video.source || video.source == 'web') ? true : false}
              >
                {clickedCopyGif ? t('video.settings.share.copied_gif') : t('video.settings.share.copy_gif.button')}
              </SharingModalButton>
              {isMobileView ? null : <span className={styles.text}>{t('video.settings.share.copy_thumbnail_code')}</span>}
            </div>
            <hr />
            <div className={styles.size}>
              <Checkbox id="responsive" onChange={handleChangeCheckbox} checked={checked.responsive.checked} ariaLabel={t('video.settings.share.responsive_size')} />
              <span>{t('video.settings.share.responsive_size')}</span>
            </div>
            <div className={styles.size}>
              <Checkbox id="fixed" onChange={handleChangeCheckbox} checked={checked.fixed.checked} ariaLabel={t('video.settings.share.fixed_size')} />
              <span>{t('video.settings.share.fixed_size')}</span>
            </div>
            {checked.fixed.checked ? <ResponsiveSettings setGifSize={setGifSize} gifSize={gifSize} /> : null}
            <SharingModalButton
              onClick={handleCopyEmbedCode}
              clicked={clickedCopyEmbed}
              disabled={!video.thumbnail && (!video.source || video.source == 'web') ? true : false}
              ariaLabel={clickedCopyEmbed ? t('video.settings.share.copied_embed_code') : t('video.settings.share.copy_embed_code.button')}
            >
              {clickedCopyEmbed ? t('video.settings.share.copied_embed_code') : `<${t('video.settings.share.copy_embed_code.button')}>`}
            </SharingModalButton>
          </div>
        </div>
      </ModalContent>
      {!!video?.author?.username ? (
        <ModalFooter className={cx(styles.footer)}>
          <SharingFooter
            videoId={videoId}
            isCanChangeVideoPassword={isCanChangeVideoPassword}
            videoUrl={video?.shareUrl}
            isMobileView={isMobileView}
            isSkipAuthToken={isSkipAuthToken}
          />
        </ModalFooter>
      ) : null}
    </>
  );
};

export default Embed;
